import {
  CustomFieldFilter,
  NativeFieldFilter,
  SimpleSearchFilter,
} from 'module/api/filter/types';
import { QueryCombinerType } from 'module/api/filter/types';
import { logger } from 'module/logger';
import { AiFieldFilter } from 'module/types/filter-field/filter';
import { AiFieldFilterType } from 'module/types/filter-field/filter/index';
import { defaultGroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import {
  GroupCriteria,
  GroupedFilter,
} from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import {
  isFieldFilterValid,
  isFieldFilterCriteria,
} from 'module/types/filter-field/grouped-filter/utils';
import { isAiFieldFilterCriteria } from 'module/types/filter-field/grouped-filter/utils';
import { GroupFieldsMeta } from 'module/types/filter-field/transformer/filter-transformer';
import { ApiFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-filter-transformer';
import {
  ApiFilter,
  ApiGroupFilter,
} from 'module/ui/components/molecules/field-filter/simple-search-filter/simple-search-filter';
import { keyBy } from 'lodash';

export const to_grouped_filter = (
  filter: GroupedFilter,
): SimpleSearchFilter => {
  return {
    nativeFields: filter.groupCriteria
      .filter(({ criteria }) => isFieldFilterValid(criteria))
      .filter(
        ({ criteria }) =>
          isFieldFilterCriteria(criteria) && !criteria.field.userCreated,
      )
      .map(ApiGroupFilter.toNativeFieldFilterFormatForGroups)
      .filter((x) => x != null),
    customFields: filter.groupCriteria
      .filter(({ criteria }) => isFieldFilterValid(criteria))
      .filter(
        ({ criteria }) =>
          isFieldFilterCriteria(criteria) && criteria.field.userCreated,
      )
      .map(ApiGroupFilter.toCustomFieldFilterFormatForGroups)
      .filter((x) => x != null),
    aiFields: filter.groupCriteria
      .filter(({ criteria }) => isAiFieldFilterCriteria(criteria))
      .map((x) => x.criteria as AiFieldFilter),
    match: filter.type,
  };
};

const tryParseNativeFieldFilterForGroup = (
  filter: NativeFieldFilter,
  group: FilterGroupType,
  lookups: GroupFieldsMeta,
): GroupCriteria | null => {
  const field = lookups.find(({ id }) => id === group)?.fields[filter.name];

  if (!field) {
    return null;
  }

  try {
    return {
      id: group,
      criteria: ApiFilter.parse(filter, field),
    };
  } catch (err) {
    logger.error('failed to parse criteria', { filter, error: err as Error });

    return null;
  }
};

const tryParseCustomFieldFilterForGroup = (
  filter: CustomFieldFilter,
  group: FilterGroupType,
  lookups: GroupFieldsMeta,
): GroupCriteria | null => {
  const byIdLookup = lookups
    .filter((x) => x.id === group)
    .map((x) => ({
      ...x,
      fields: keyBy(Object.values(x.fields), 'fieldId'),
    }));

  const field = byIdLookup[0]?.fields[filter.fieldId];

  if (!field) {
    return null;
  }

  try {
    return {
      id: group,
      criteria: ApiFilter.parse(filter, field),
    };
  } catch (err) {
    logger.error('failed to parse criteria', { filter, error: err as Error });

    return null;
  }
};

export const from_grouped_filter =
  (lookups: GroupFieldsMeta, defaultGroup: FilterGroupType) =>
  (query: SimpleSearchFilter): GroupedFilter =>
    lookups.every((lookup) => Object.keys(lookup).length === 0)
      ? { ...defaultGroupedFilter(defaultGroup), aiFields: [] }
      : {
          type: query?.match ?? QueryCombinerType.All,
          defaultGroup,
          groupCriteria: [
            ...(query.nativeFields ?? [])
              .map((field) => {
                return tryParseNativeFieldFilterForGroup(
                  field,
                  field.sourceType
                    ? (field.sourceType as unknown as FilterGroupType)
                    : defaultGroup,
                  lookups,
                );
              })
              .filter((x) => x != null)
              .map((x) => x as GroupCriteria),
            ...(query.customFields ?? [])
              .map((field) =>
                tryParseCustomFieldFilterForGroup(
                  field,
                  field.sourceType
                    ? (field.sourceType as unknown as FilterGroupType)
                    : defaultGroup,
                  lookups,
                ),
              )
              .filter((x) => x != null)
              .map((x) => x as GroupCriteria),
            ...(query.aiFields ?? []).map(
              (ai) =>
                ({
                  id: ai.id,
                  criteria: {
                    id: ai.id,
                    value: ai.value,
                    isAiGenerated: ai.isAiGenerated ?? false,
                    type: AiFieldFilterType.Ai,
                    fieldName: ai.fieldName,
                  },
                }) as GroupCriteria,
            ),
          ],
        };

export const getApiGroupedFilterTransformer = (
  lookup: GroupFieldsMeta,
  defaultGroup: FilterGroupType,
): ApiFilterTransformer<GroupedFilter> => ({
  from: (simpleSearchFilter) =>
    from_grouped_filter(lookup, defaultGroup)(simpleSearchFilter),
  to: to_grouped_filter,
});
